import { useId } from 'react'

import { cn } from '../cn'

// ToggleSwitch — a single binary setting that applies IMMEDIATELY (2FA,
// sandbox, an on/off rule) — design/components/toggle.html. Differs from a
// checkbox: no "Save" step. On = success green; the knob also moves, so state
// is never colour-only. Use the row pattern (label left, switch right) in
// settings; if the effect is destructive, still confirm it.

export interface ToggleSwitchProps {
  checked: boolean
  /** Applies immediately. */
  onChange: (checked: boolean) => void
  /** Required — shown in the row/inline, or used as aria-label when hidden. */
  label: string
  description?: string
  size?: 'md' | 'sm'
  disabled?: boolean
  /** Render only the switch (label becomes aria-label) — for toolbars/cells. */
  hideLabel?: boolean
  className?: string
}

const track = {
  md: 'h-5 w-[34px]',
  sm: 'h-[18px] w-[30px]',
}
const knob = {
  md: 'size-4',
  sm: 'size-[14px]',
}
const knobOn = {
  md: 'translate-x-[14px]',
  sm: 'translate-x-3',
}

interface SwitchProps {
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean | undefined
  size?: 'md' | 'sm' | undefined
  className?: string | undefined
  'aria-label'?: string | undefined
  'aria-labelledby'?: string | undefined
  'aria-describedby'?: string | undefined
}

function Switch({ checked, onChange, disabled, size = 'md', className, ...aria }: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex flex-none items-center rounded-full transition-colors duration-quick ease-vysto',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue',
        'disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-ok' : 'bg-input-line',
        track[size],
        className,
      )}
      {...aria}
    >
      <span
        aria-hidden
        className={cn(
          'absolute left-[2px] rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.18)] transition-transform duration-quick ease-vysto',
          knob[size],
          checked && knobOn[size],
        )}
      />
    </button>
  )
}

/** role=switch for a binary setting that applies IMMEDIATELY — no Save step
 *  (unlike a checkbox). Renders inline, as a settings row when `description`
 *  is set, or switch-only via `hideLabel` (label becomes the aria-label).
 *  Destructive effects still need their own confirm. */
export function ToggleSwitch({
  checked,
  onChange,
  label,
  description,
  size = 'md',
  disabled,
  hideLabel,
  className,
}: ToggleSwitchProps) {
  const labelId = useId()
  const descId = useId()

  if (hideLabel) {
    return (
      <Switch
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        size={size}
        className={className}
        aria-label={label}
      />
    )
  }

  if (description) {
    return (
      <div
        className={cn('flex w-full items-center gap-3.5', className)}
        aria-disabled={disabled ? true : undefined}
      >
        <span className={cn('flex flex-col gap-px', disabled && 'opacity-50')}>
          <span id={labelId} className="text-[12.5px] font-semibold text-text">
            {label}
          </span>
          <span id={descId} className="text-[10.5px] text-text-3">
            {description}
          </span>
        </span>
        <Switch
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          size={size}
          className="ml-auto"
          aria-labelledby={labelId}
          aria-describedby={descId}
        />
      </div>
    )
  }

  return (
    <span className={cn('inline-flex items-center gap-[9px] text-[12px] text-text', className)}>
      <Switch
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        size={size}
        aria-labelledby={labelId}
      />
      <span id={labelId} className={cn(disabled && 'opacity-50')}>
        {label}
      </span>
    </span>
  )
}
